import React from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  User,
  Edit,
  Repeat,
  Video,
  FileCheck,
  Tag,
  Lock,
} from 'lucide-react'
import { meetingStatusVariant, meetingTypeVariant } from '@/lib/status-variants'
import { formatDateInTz, formatTimeInTz } from '../../../utils/timezone'

export interface MeetingCardData {
  id: string
  number: number
  displayNumber: string
  title: string
  type: string
  status: string
  scheduledAt: string
  durationMinutes: number
  isVirtual: boolean
  confidential?: boolean
  location: string | null
  virtualLink: string | null
  recurrence: string
  attendeeCount: number
  distributedAt: string | null
  finalizedAt: string | null
  organizer: { id: string; name: string } | null
  project?: { id: string; resolvedTimezone?: string | null } | null
}

interface Props {
  meeting: MeetingCardData
  onView: (m: MeetingCardData) => void
  onEdit?: (m: MeetingCardData) => void
}

const MeetingCard: React.FC<Props> = ({ meeting, onView, onEdit }) => {
  const { t, i18n } = useTranslation()
  const tz = meeting.project?.resolvedTimezone
  const formatDate = (iso: string) => formatDateInTz(iso, tz, 'PP', i18n.language)
  const formatTime = (iso: string) => formatTimeInTz(iso, tz, i18n.language)

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onView(meeting)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onView(meeting)
        }
      }}
      className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/30 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-xs font-mono text-muted-foreground">{meeting.displayNumber}</span>
          <h3 className="font-semibold truncate">{meeting.title}</h3>
          <Badge variant={meetingTypeVariant(meeting.type)} className="gap-1">
            <Tag className="h-3 w-3" />
            {t(`Meeting_type_${meeting.type.toLowerCase()}`)}
          </Badge>
          <Badge variant={meetingStatusVariant(meeting.status)}>
            {t(`Meeting_status_${meeting.status.toLowerCase()}`)}
          </Badge>
          {meeting.confidential && (
            <Badge variant="neutral" className="gap-1">
              <Lock className="h-3 w-3" />
              {t('Meeting_confidential_badge')}
            </Badge>
          )}
          {meeting.recurrence !== 'NONE' && (
            <Badge variant="neutral">
              <Repeat className="h-3 w-3 mr-1" />
              {t(`Meeting_recurrence_${meeting.recurrence.toLowerCase()}`)}
            </Badge>
          )}
          {meeting.distributedAt && (
            <Badge variant="success">
              <FileCheck className="h-3 w-3 mr-1" />
              {t('Meeting_distributed_badge')}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
          <div className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            <span>{formatDate(meeting.scheduledAt)}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            <span>
              {formatTime(meeting.scheduledAt)} · {meeting.durationMinutes} min
            </span>
          </div>
          <div className="flex items-center gap-1">
            {meeting.isVirtual ? (
              <Video className="h-3.5 w-3.5" />
            ) : (
              <MapPin className="h-3.5 w-3.5" />
            )}
            <span className="truncate max-w-[200px]">
              {meeting.isVirtual ? t('Meeting_virtual_label') : meeting.location || '—'}
            </span>
          </div>
          {meeting.organizer && (
            <div className="flex items-center gap-1">
              <User className="h-3.5 w-3.5" />
              <span>{meeting.organizer.name}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            <span>{meeting.attendeeCount}</span>
          </div>
        </div>
      </div>

      {onEdit && (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onEdit(meeting)
            }}
            disabled={meeting.status === 'CANCELLED'}
            title={
              meeting.status === 'CANCELLED'
                ? t('Meeting_action_edit_disabled_cancelled')
                : t('Common_edit')
            }
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  )
}

export default MeetingCard
